import { Box, ButtonProps, IconButton, Input } from '@chakra-ui/react';
import * as React from 'react';

import { EPrepImagesSource } from '@/graphql-types/globalTypes';
import { useImageUpload } from '@/hooks/useImageUpload';
import { CameraIcon } from '@/imports/ui/chakra/feather';

export const ImageUploadButton = React.forwardRef<
  HTMLInputElement,
  {
    value: ({ imageId: string } | { videoId: string })[];
    onChange: (value: ({ imageId: string } | { videoId: string })[]) => void;
    onBlur: () => void;
    source: EPrepImagesSource;
  } & ButtonProps
>(({ value, onChange, onBlur, source, ...buttonProps }, ref) => {
  const internalRef = React.useRef<HTMLInputElement>(null);
  React.useImperativeHandle(ref, () => internalRef.current as HTMLInputElement);

  const { uploadImages } = useImageUpload(source);

  const [isLoading, setIsLoading] = React.useState(false);

  return (
    <Box>
      <Input
        type="file"
        multiple={false}
        accept="image/*"
        onChange={async (event) => {
          setIsLoading(true);
          const files = event.currentTarget.files
            ? Array.from(event.currentTarget.files)
            : [];
          const promisedResources = files.map(async (file) => {
            const resource = file.type.includes('image')
              ? await uploadImages([file])
              : null;

            return resource
              ? file.type.includes('image')
                ? { imageId: resource[0] }
                : { videoId: resource[0] }
              : null;
          });
          const resources = (await Promise.all(promisedResources)).filter(
            (resource): resource is { imageId: string } | { videoId: string } =>
              !!resource
          );

          setIsLoading(false);
          return onChange && onChange([...(value || []), ...resources]);
        }}
        onBlur={onBlur}
        ref={internalRef}
        display="none"
      />
      <IconButton
        aria-label="Upload Images"
        icon={<CameraIcon />}
        onClick={() => internalRef.current?.click()}
        isLoading={isLoading}
        {...buttonProps}
      />
    </Box>
  );
});

ImageUploadButton.displayName = 'ImageUploadButton';
